/**
 * A list/table card that shows a compact, height-capped preview with an Expand
 * button. Expanding opens a large modal whose body scrolls internally while the
 * page behind stays fixed (Radix locks page scroll). Used for every analytics
 * list so they all read as an overview that opens into a full, clear view.
 */
import { Maximize2 } from 'lucide-react';
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IconButton } from './Button.js';
import { cn } from './cn.js';
import { Spinner } from './Feedback.js';
import { Modal } from './Modal.js';

export function ExpandableCard({
  title,
  subtitle,
  loading,
  empty,
  headerActions,
  previewHeight = 360,
  className,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  loading?: boolean;
  empty?: ReactNode;
  /** Controls (search, filters, add…) shown in the header and again in the modal. */
  headerActions?: ReactNode;
  previewHeight?: number;
  className?: string;
  children: ReactNode;
}): ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  // The "Show all" fade only appears when the preview actually clips content.
  // Measure on mount and on any size change (ResizeObserver); guarded for jsdom.
  useLayoutEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const measure = (): void => setOverflowing(el.scrollHeight > el.clientHeight + 4);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <>
      <div className={cn('rounded-xl border border-border bg-surface', className)}>
        <div className="flex items-start justify-between gap-3 px-5 pt-4">
          <div className="min-w-0">
            <h3 className="font-display text-base font-semibold text-fg">{title}</h3>
            {subtitle ? <p className="text-xs text-muted">{subtitle}</p> : null}
          </div>
          <div className="flex flex-shrink-0 items-center gap-1">
            {headerActions}
            {!loading && !empty ? (
              <IconButton label={t('common.showAll')} onClick={() => setOpen(true)}>
                <Maximize2 className="h-4 w-4" />
              </IconButton>
            ) : null}
          </div>
        </div>

        <div className="px-3 pb-3 pt-3">
          {loading ? (
            <div className="grid h-40 place-items-center">
              <Spinner className="h-6 w-6 text-primary" />
            </div>
          ) : empty ? (
            <div className="grid h-40 place-items-center text-sm text-muted">{empty}</div>
          ) : (
            <div className="relative">
              <div
                ref={previewRef}
                className="overflow-hidden"
                style={{ maxHeight: previewHeight }}
              >
                {children}
              </div>
              {overflowing ? (
                <button
                  type="button"
                  onClick={() => setOpen(true)}
                  aria-label={t('common.showAll')}
                  className="absolute inset-x-0 bottom-0 flex h-20 cursor-pointer items-end justify-center bg-gradient-to-t from-surface via-surface/85 to-transparent"
                >
                  <span className="mb-1 rounded-full border border-border bg-surface-2 px-3 py-1 text-xs font-medium text-muted shadow-card transition-colors hover:text-fg">
                    {t('common.showAll')}
                  </span>
                </button>
              ) : null}
            </div>
          )}
        </div>
      </div>

      <Modal open={open} onOpenChange={setOpen} title={title} description={subtitle} size="xl">
        {headerActions ? (
          <div className="mb-3 flex flex-wrap items-center gap-2">{headerActions}</div>
        ) : null}
        <div className="max-h-[75vh] overflow-y-auto">{children}</div>
      </Modal>
    </>
  );
}
